import { produceNextCacheEntry } from './entries/produceNextCacheEntry';
import { tryMarkStale } from './entries/tryMarkStale';
import { toError } from '../resolvers/definition/resolverState';
export const breakerKey = (a, b, tag) => {
  const [first, second] = a < b ? [a, b] : [b, a];
  return `link::${first}::${second}::${tag}`;
};
export const establishEdge = (ctx, childKey, parentKey, edge, childHash, tag, parentContext, childContext) => {
  const parentEmitter = ctx.nodeStore.getEmitter(parentKey);
  const childEmitter = ctx.nodeStore.getEmitter(childKey);
  const offs = [];
  const cleanup = () => offs.forEach(off => off());
  offs.push(ctx.teardown.on(parentKey, cleanup));
  offs.push(ctx.teardown.on(childKey, cleanup));
  offs.push(ctx.breaker.on(breakerKey(parentKey, childKey, tag), cleanup));
  const {
    behavior,
    resolve
  } = edge;
  if (behavior.state && resolve) {
    const applyTransform = ({
      changeContext,
      dataChanged = true,
      flagsChanged = true
    }) => {
      const childEntry = ctx.nodeStore.get(childKey);
      if (!childEntry) {
        var _ctx$logger;
        (_ctx$logger = ctx.logger) === null || _ctx$logger === void 0 || _ctx$logger.error('child entry not found');
        return;
      }
      if (childEntry.meta.changeContext.changeId >= changeContext.changeId) {
        return;
      }
      const parentEntry = ctx.nodeStore.get(parentKey);
      if (!parentEntry) {
        var _ctx$logger2;
        (_ctx$logger2 = ctx.logger) === null || _ctx$logger2 === void 0 || _ctx$logger2.error('parent entry not found');
        return;
      }
      let result;
      try {
        var _resolve;
        const nextState = (_resolve = resolve({
          dataChanged,
          flagsChanged,
          parent: {
            state: parentEntry.state,
            params: parentEntry.meta.params,
            context: parentContext
          },
          child: {
            state: childEntry.state,
            params: childEntry.meta.params,
            context: childContext
          }
        })) !== null && _resolve !== void 0 ? _resolve : childEntry.state;
        result = produceNextCacheEntry({
          hash: childHash,
          currentEntry: childEntry,
          params: childEntry.meta.params,
          changeContext,
          nextState
        });
      } catch (error) {
        var _ctx$logger4;
        if (changeContext.optimistic) {
          var _ctx$logger3;
          // Child is left unchanged. On rollback, the parent reverts to its
          // pre-optimistic state and re-derives the child via full propagation.
          // If resolve still throws with the rolled-back data, the non-optimistic
          // error path below will apply the error state to the child.
          (_ctx$logger3 = ctx.logger) === null || _ctx$logger3 === void 0 || _ctx$logger3.error('optimistic propagation: resolve threw', {
            error: error.message,
            parentKey,
            childKey
          });
          return;
        }
        (_ctx$logger4 = ctx.logger) === null || _ctx$logger4 === void 0 || _ctx$logger4.error('error applying transform', {
          error: error.message
        });
        const nextState = toError(error)(childEntry.state);
        result = produceNextCacheEntry({
          hash: childHash,
          currentEntry: childEntry,
          params: childEntry.meta.params,
          changeContext,
          nextState
        });
        if (result.dataChanged || result.flagsChanged) {
          ctx.nodeStore.set(childKey, result.nextEntry);
          ctx.onChange(childKey, result.nextEntry, result);
        }

        // Don't cascade — the error was produced by this edge's resolve,
        // not by the parent's state.
        return;
      }
      if (!result.dataChanged && !result.flagsChanged) {
        return;
      }
      ctx.nodeStore.set(childKey, result.nextEntry);
      return result;
    };

    // State propagation: parent data changes > compute child's next state
    offs.push(parentEmitter.on('state', event => {
      var _ctx$devtools;
      const result = applyTransform(event);
      if (!result) {
        return;
      }
      if (result.dataChanged && !event.changeContext.optimistic) {
        ctx.syncTags(childKey, event.changeContext);
      }
      ctx.onChange(childKey, result.nextEntry, result);
      (_ctx$devtools = ctx.devtools) === null || _ctx$devtools === void 0 || _ctx$devtools.trace({
        parent: 'propagation',
        type: 'state',
        changeContext: event.changeContext,
        instanceKey: childKey,
        parentInstanceKey: parentKey
      });

      // Notify all listeners of change
      childEmitter.emit('state', {
        changeContext: event.changeContext,
        dataChanged: result.dataChanged,
        flagsChanged: result.flagsChanged
      });
    }));

    // Initialization: child requests initialization > parent replays its state
    offs.push(childEmitter.on('init', event => {
      var _ctx$nodeStore$get;
      const changeContext = (_ctx$nodeStore$get = ctx.nodeStore.get(parentKey)) === null || _ctx$nodeStore$get === void 0 ? void 0 : _ctx$nodeStore$get.meta.changeContext;
      if (changeContext && applyTransform({
        changeContext
      })) {
        event.onInit(parentKey);
      }
    }));
  }
  if (edge.behavior.invalidation) {
    const invalidate = changeContext => {
      var _ctx$devtools2;
      const childEntry = ctx.nodeStore.get(childKey);
      if (!childEntry) {
        return false;
      }
      const nextEntry = tryMarkStale(childEntry, childHash, changeContext);
      if (!nextEntry) {
        return false;
      }
      (_ctx$devtools2 = ctx.devtools) === null || _ctx$devtools2 === void 0 || _ctx$devtools2.trace({
        changeContext,
        instanceKey: childKey,
        parent: 'propagation',
        type: 'invalidation',
        parentInstanceKey: parentKey
      });
      ctx.nodeStore.set(childKey, nextEntry);
      ctx.onChange(childKey, nextEntry, {
        dataChanged: false,
        flagsChanged: true
      });
      ctx.onInvalidated(childKey, changeContext);
      ctx.nodeStore.getEmitter(childKey).emit('invalidation', {
        changeContext
      });
      return true;
    };

    // Invalidation: parent invalidated > invalidate child
    offs.push(parentEmitter.on('invalidation', ({
      changeContext
    }) => {
      if (!changeContext.optimistic) {
        invalidate(changeContext);
      }
    }));

    // Cascading invalidation: parent data changes > mark child stale
    if (!edge.behavior.state) {
      offs.push(parentEmitter.on('state', ({
        dataChanged,
        changeContext
      }) => {
        if (dataChanged && changeContext.canCascade && !changeContext.optimistic) {
          invalidate(changeContext);
        }
      }));
    }
  }
};